import React from 'react';
import "./styles.css";
import { BrandingStyle } from "@bcm-dev/bcms-vite";

/**
 * @function UIButton
 * @param {STRING} className - Additional classes to add to the UIButton
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @param {React.Component} children - React components that's being wrapped by the UIButton component.
  * @param {dataPoint} onClick -data-point to add to wrapper for captures. 
 * @return {React.Component}
 * @exported true
 * @description The UIButton is the base component for all of the button components. It is used as a blueprint.
 * @example
 * <UIButton className="added-style">Activate<UIButton>
 */
export default function UIButton(props) {
  const className = props.className ?? "";
  let dataPoint =  props.dataPoint ? {"data-point":props.dataPoint}:{};
  return (
    <button style={props.style} className={className + " ui-button flex-width"} onClick={props.onClick}>
      <span {...dataPoint}>{props.children}</span>
    </button>
  );
}

/**
 * @function UIPrimaryButton
 * @param {STRING} className - Additional classes to add to the UIPrimaryButton
 * @param {STRING} text - Passes the button text to the UIButton
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @param {dataPoint} onClick -data-point to add to wrapper for captures.
 * @return {React.Component}
 * @exported true
 * @description This is the primary button option. This has a background color with an inverted text color.
 * @example
 * <UIPrimaryButton className="added-style" text="Activate" onClick={function} />
 */
export function UIPrimaryButton(props) {
  const className = props.className ?? "";
  const text = props.text ?? "[text]";

  return (
    <BrandingStyle 
      style={{ "backgroundColor" : "primary_button_bg_chw23", "borderColor" : "primary_button_bg_chw23"}} 
      className={className + " ui-primary-button"} 
      element={
        <UIButton onClick={props.onClick} dataPoint={props.dataPoint}>
          <BrandingStyle style={{ "color" : "primary_button_text_chw23"}} element={<span>{ text }</span> } />
        </UIButton>
      } />

   
  );
}

/**
 * @function UISecondaryButton
 * @param {STRING} className - Additional classes to add to the UISecondaryButton
 * @param {STRING} text - Passes the button text to the UIButton
 * @param {dataPoint} onClick -data-point to add to wrapper for captures.
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @return {React.Component}
 * @exported true
 * @description This button has a border and no background. It is used as the secondary button option. 
 * @example
 * <UISecondaryButton className="added-style" text="Edit" onClick={function} />
 */
export function UISecondaryButton(props) {
  const className = props.className ?? "";
  const text = props.text ?? "[text]";

  return (
    <BrandingStyle 
    style={{"backgroundColor" : "secondary_button_bg_chw23", "borderColor" : "secondary_button_text_chw23" }}
    className={className + " ui-secondary-button"}
    element={
      <UIButton  onClick={props.onClick} dataPoint={props.dataPoint}>
        <BrandingStyle style={{ "color" : "secondary_button_text_chw23"}} element={<span>{ text }</span> } />
      </UIButton>
    } />
    
  );
}

/**
 * @function UIIconButton
 * @param {STRING} className - Additional classes to add to the UIIconButton
 * @param {STRING} text - Passes the button text to the UIButton
 * @param {STRING} icon - passes the icon id to the UIButton
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @param {dataPoint} onClick -data-point to add to wrapper for captures.
 * @return {React.Component}
 * @exported true
 * @description The UIIconButton is a borderless, background-less button that shows a defined icon above the text.
 * @example
 * <UIIconButton className="added-style" text="Email" icon="emailIcon" onClick={function} />
 */
export function UIIconButton(props) {
  const className = props.className ?? "";
  const text = props.text ?? "[text]";
  const icon = props.icon ?? "";

  return (
    <UIButton className={className + " ui-icon-button"} onClick={props.onClick} dataPoint={props.dataPoint}>
      <div className="flex-container flex-vertical flex-width align-center-container">
        <div className="flex-height icon-container">
          { icon }
        </div>
        <div className="flex-height icon-container">
          <span>{ text }</span>
        </div>
      </div>
    </UIButton>
  );
}


/**
 * @function UICancelButton
 * @param {STRING} className - Additional classes to add to the UIButton
 * @param {STRING} text - Passes the button text to the UIButton
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @return {React.Component}
 * @exported true
 * @description This is the button that is used primary for the "cancel". It is a borderless, background-less button. 
 * @example
 * <UICancelButton className="added-style" text="Cancel" onClick={function} />
 */
export function UICancelButton(props) {
  const className = props.className ?? "";
  const text = props.text ?? "[text]";
  
  return (
    <UIButton className={className + " ui-cancel-button"} onClick={props.onClick} dataPoint={props.dataPoint}>
      <BrandingStyle style={{ "color" : "secondary_button_text_chw23"}} element={<span>{ text }</span> } />
    </UIButton>
  );
}
